import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { createMessageNotification } from './notificationService';
import { getUserById } from './userService';
import { getTipAccessRecord, grantTipAccess } from './tipAccessService';

const messagesCollection = collection(db, 'messages');
const messageRequestsCollection = collection(db, 'messageRequests');

function normalizeRole(role) {
    return role === 'artist' ? 'artist' : 'listener';
}

async function getUserRole(uid) {
    if (!uid) return 'listener';
    try {
        const user = await getUserById(uid);
        return normalizeRole(user?.role);
    } catch (err) {
        console.warn('Failed to read role for user', uid, err);
        return 'listener';
    }
}

function buildMessagePayload({ text, senderId, receiverId, senderRole, receiverRole, tipId }) {
    const cleanedText = (text ?? '').toString().trim();
    const payload = {
        text: cleanedText,
        senderId,
        receiverId,
        timestamp: serverTimestamp(),
        senderRole: normalizeRole(senderRole),
        receiverRole: normalizeRole(receiverRole),
    };
    if (tipId) {
        payload.tipId = tipId;
    }
    return payload;
}

async function persistMessageDoc(payload) {
    const docRef = await addDoc(messagesCollection, payload);

    // Create notification for all message receivers (artist to artist, artist to listener, listener to listener)
    // Listener to artist messages go through request box if no tip, so they don't reach here
    createMessageNotification(payload.senderId, payload.receiverId, payload.text).catch((err) => {
        console.error('Failed to create message notification', err);
    });

    return docRef;
}

export async function createMessage({ text, receiverId }) {
    if (!auth.currentUser) {
        throw new Error('You must be signed in to send messages.');
    }
    if (!receiverId) {
        throw new Error('Receiver is required.');
    }

    const trimmedText = text?.trim();
    if (!trimmedText) {
        throw new Error('Message cannot be empty.');
    }

    const senderId = auth.currentUser.uid;
    const [senderRole, receiverRole] = await Promise.all([
        getUserRole(senderId),
        getUserRole(receiverId),
    ]);

    let tipAccessRecord = null;
    if (senderRole === 'listener' && receiverRole === 'artist') {
        tipAccessRecord = await getTipAccessRecord(senderId, receiverId);
        const allowed = !!(tipAccessRecord && tipAccessRecord.allowed);
        if (!allowed) {
            const requestRef = await addDoc(messageRequestsCollection, {
                senderId,
                receiverId,
                senderRole,
                receiverRole,
                text: trimmedText,
                timestamp: serverTimestamp(),
                status: 'pending',
            });
            return { type: 'request', id: requestRef.id };
        }
    }

    const payload = buildMessagePayload({
        text: trimmedText,
        senderId,
        receiverId,
        senderRole,
        receiverRole,
        tipId: tipAccessRecord?.tipId || null,
    });
    const messageRef = await persistMessageDoc(payload);
    return { type: 'message', id: messageRef.id };
}

export async function fetchMessages(currentUid, otherUid) {
    if (!currentUid || !otherUid) return [];
    // Firestore doesn't support multiple 'in' queries, so we need to fetch separately
    // and combine the results
    const q1 = query(
        messagesCollection,
        where('senderId', '==', currentUid),
        where('receiverId', '==', otherUid),
        orderBy('timestamp', 'asc')
    );
    const q2 = query(
        messagesCollection,
        where('senderId', '==', otherUid),
        where('receiverId', '==', currentUid),
        orderBy('timestamp', 'asc')
    );
    const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
    const allDocs = [...snap1.docs, ...snap2.docs];
    return allDocs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => {
            const ta = a.timestamp?.toMillis ? a.timestamp.toMillis() : a.timestamp || 0;
            const tb = b.timestamp?.toMillis ? b.timestamp.toMillis() : b.timestamp || 0;
            return ta - tb;
        });
}

export function subscribeToMessages(currentUid, otherUid, onData, onError) {
    if (!currentUid || !otherUid) {
        return () => {};
    }

    // Firestore doesn't support multiple 'in' queries, so we need to subscribe to two queries
    // and merge the results
    const q1 = query(
        messagesCollection,
        where('senderId', '==', currentUid),
        where('receiverId', '==', otherUid),
        orderBy('timestamp', 'asc')
    );
    const q2 = query(
        messagesCollection,
        where('senderId', '==', otherUid),
        where('receiverId', '==', currentUid),
        orderBy('timestamp', 'asc')
    );

    const allDocsMap = new Map();

    const updateAndNotify = () => {
        const docs = Array.from(allDocsMap.values()).sort((a, b) => {
            const ta = a.timestamp?.toMillis ? a.timestamp.toMillis() : a.timestamp || 0;
            const tb = b.timestamp?.toMillis ? b.timestamp.toMillis() : b.timestamp || 0;
            return ta - tb;
        });
        onData?.(docs);
    };

    const unsub1 = onSnapshot(
        q1,
        (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'removed') {
                    allDocsMap.delete(change.doc.id);
                } else {
                    allDocsMap.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
                }
            });
            updateAndNotify();
        },
        (err) => {
            console.error('Messages subscription error (q1)', err);
            onError?.(err);
        }
    );

    const unsub2 = onSnapshot(
        q2,
        (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'removed') {
                    allDocsMap.delete(change.doc.id);
                } else {
                    allDocsMap.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
                }
            });
            updateAndNotify();
        },
        (err) => {
            console.error('Messages subscription error (q2)', err);
            onError?.(err);
        }
    );

    return () => {
        unsub1();
        unsub2();
    };
}

export async function acceptMessageRequest(request) {
    if (!request?.id) throw new Error('Invalid request.');
    const requestRef = doc(db, 'messageRequests', request.id);
    let createdMessageId = null;
    let notificationContext = null;

    await runTransaction(db, async (tx) => {
        const snap = await tx.get(requestRef);
        if (!snap.exists()) {
            throw new Error('Request not found.');
        }
        const data = snap.data();
        const payload = buildMessagePayload({
            text: data.text || '',
            senderId: data.senderId,
            receiverId: data.receiverId,
            senderRole: data.senderRole || 'listener',
            receiverRole: data.receiverRole || 'artist',
        });
        const messageRef = doc(messagesCollection);
        tx.set(messageRef, payload);
        tx.delete(requestRef);
        createdMessageId = messageRef.id;
        notificationContext = {
            senderId: data.senderId,
            receiverId: data.receiverId,
            text: data.text || '',
            receiverRole: data.receiverRole || 'artist',
        };
    });

    try {
        await grantTipAccess({
            senderId: notificationContext.senderId,
            receiverId: notificationContext.receiverId,
            source: 'artist_accept',
            grantedBy: notificationContext.receiverId,
        });
    } catch (err) {
        console.warn('Failed to grant tip access on manual accept', err);
    }

    if (notificationContext?.receiverRole === 'listener') {
        createMessageNotification(
            notificationContext.senderId,
            notificationContext.receiverId,
            notificationContext.text
        ).catch((err) => {
            console.error('Failed to create message notification', err);
        });
    }

    return createdMessageId;
}

export async function rejectMessageRequest(requestId) {
    if (!requestId) throw new Error('Invalid request id.');
    await deleteDoc(doc(db, 'messageRequests', requestId));
}

export async function createMessageFromTip({ senderId, receiverId, text, tipId, senderRole, receiverRole }) {
    if (!senderId || !receiverId) throw new Error('Sender and receiver are required.');
    const payload = buildMessagePayload({
        text: text?.trim() || '',
        senderId,
        receiverId,
        senderRole: senderRole || (await getUserRole(senderId)),
        receiverRole: receiverRole || (await getUserRole(receiverId)),
        tipId,
    });
    const messageRef = await persistMessageDoc(payload);
    return messageRef.id;
}

export async function convertPendingRequestsAfterTip({ senderId, receiverId, tipId }) {
    if (!senderId || !receiverId || !tipId) return;
    const pendingQuery = query(
        messageRequestsCollection,
        where('senderId', '==', senderId),
        where('receiverId', '==', receiverId),
        where('status', '==', 'pending')
    );
    const snap = await getDocs(pendingQuery);
    if (snap.empty) return;

    const senderRole = await getUserRole(senderId);
    const receiverRole = await getUserRole(receiverId);

    try {
        await grantTipAccess({
            senderId,
            receiverId,
            tipId,
            source: 'tip',
            grantedBy: receiverId,
        });
    } catch (err) {
        console.warn('Failed to grant tip access while converting requests', err);
    }

    await Promise.all(
        snap.docs.map(async (docSnap) => {
            const data = docSnap.data();
            const payload = buildMessagePayload({
                text: data.text || '',
                senderId,
                receiverId,
                senderRole,
                receiverRole,
                tipId,
            });
            await persistMessageDoc(payload);
            await deleteDoc(docSnap.ref);
        })
    );
}

